import { SortableTh, SortingDirection, Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { IconButtonRegular as IconButton } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import ASOneIcon from '@components/icons/ASOne.svg';
import HistoryIcon from '@components/icons/History.svg';
import LockedJob from '@components/icons/LockedJob.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import { TagAccent } from '@components/core-ui/Tag/variations/TagAccent';
import { Tooltip } from '@components/core-ui/Tooltip';
import { JobStatusText } from '@components/JobStatusText';
import { convertDuration } from '@helpers/convertDuration';
import { formatDateMsDsYHI } from '@helpers/formatDateMsDsYHI';
import { formatIsoDateToAmerican } from '@helpers/formatIsoDateToAmerican';
import { getAmericanTime } from '@helpers/getAmericanTimezone';
import { jobCanBeReassigned } from '@helpers/jobStatus/jobCanBeReassigned';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { REPORTING_TYPE_ENUM } from 'app/types/enums/reportingTypes';
import { JobStatusActionsButton } from './JobStatusActionsButton';
import { AssignCell } from './hooks/styles/AssignCell';
import { AssignLink } from './hooks/styles/AssignLink';
import { DeadlineCell } from './hooks/styles/DeadlineCell';
import { IconsWrapper } from './hooks/styles/IconsWrapper';
import { IdField } from './hooks/styles/IdField';
import { JobName } from './hooks/styles/JobName';
import { StatusCell } from './hooks/styles/StatusCell';
import { StatusCellContent } from './hooks/styles/StatusCellContent';
import { StatusDate } from './hooks/styles/StatusDate';
import { StyledLink } from './hooks/styles/StyledLink';
import { TagsField } from './hooks/styles/TagsField';
import { routes } from '@routes';
import { PagePagination } from '@components/core-ui/Pagination';
import { JobResponseDto } from 'app/API';
import { TPaginatedResponse } from 'app/types/API/TPaginatedResponse';
import React from 'react';
import { useJobsTableHead } from './hooks/useJobsTableHead';
import { ButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/ButtonsTRow';
import { canShowButtonsTRow } from '@pages/User/JobsListPage/components/Content/components/JobsTable/helpers/canShowButtonsTRow';
import { canShowCheckboxColumn } from '@pages/User/JobsListPage/components/Content/components/JobsTable/helpers/canShowCheckboxColumn';
import ChevronLeft from '@components/icons/ChevronLeft.svg';
import { cn } from '@helpers/cn';

type TProps = {
  jobs: TPaginatedResponse<JobResponseDto>;
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  orderBy: string;
  orderType: string;
  toggleSort: (field: string) => void;
  reload: () => Promise<void>;
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  checkedJobs: string[];
  setCheckedJobs: (v: string[]) => void;
};

export const JobsTable = ({
  jobs,
  page,
  setPage,
  pageSize,
  setPageSize,
  orderBy,
  orderType,
  toggleSort,
  reload,
  search,
  setSearch,
  checkedJobs,
  setCheckedJobs,
}: TProps) => {
  const {
    checkJob,
    checkAllJobs,
    editAssignee,
    editDeadline,
    handleShowHistory,
    generateJobLink,
    renderInlineEditRow,
    jobsAvailableToEdit,
    hiddenButtonsVisible,
    setHiddenButtonsVisible,
    isVsAdmin,
    canViewAllJobsHistory,
    canManageWorkspaces,
    me,
    organization,
    openModal,
    closeModal,
  } = useJobsTableHead({
    reload,
    search,
    jobs: jobs.data,
    checkedJobs,
    setCheckedJobs,
  });

  const showCheckboxColumn = canShowCheckboxColumn(organization?.permissions);

  return (
    <>
      <TableWrapper>
        <Table className="max-md:[&>tbody>tr>td:empty]:hidden max-md:[&>tbody>tr>td:has(>div:empty)]:hidden">
          <Thead>
            <Tr>
              {showCheckboxColumn && (
                <Th style={{ width: '1%' }}>
                  <CheckBox
                    name="select-all"
                    checked={checkedJobs.length === jobsAvailableToEdit.length}
                    onChange={() => checkAllJobs()}
                  />
                </Th>
              )}
              <SortableTh onClick={() => toggleSort('name')}>
                Job Title
                {orderBy === 'name' && <SortingDirection reverse={orderType === 'desc'} />}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('deadline')}>
                Due
                {orderBy === 'deadline' && <SortingDirection reverse={orderType === 'desc'} />}
              </SortableTh>
              <Th style={{ width: '1%' }}>ASSIGNEE</Th>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('createdAt')}>
                Created
                {orderBy === 'createdAt' ? <SortingDirection reverse={orderType === 'desc'} /> : null}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('duration')}>
                Duration
                {orderBy === 'duration' ? <SortingDirection reverse={orderType === 'desc'} /> : null}
              </SortableTh>
              <SortableTh style={{ width: '1%' }} onClick={() => toggleSort('status')}>
                Status
                {orderBy === 'status' ? <SortingDirection reverse={orderType === 'desc'} /> : null}
              </SortableTh>
              <Th style={{ width: '1%' }} />
              <Th style={{ width: '1%' }} />
            </Tr>
          </Thead>
          <Tbody>
            {jobs.data?.map((job: any, index: number) => {
              const showEditAssigneeButton = organization?.permissions?.reassignJobs && jobCanBeReassigned(job.status);
              const assignee = job?.assignee || job?.draftAssignee;
              const showHistory = canViewAllJobsHistory || me?.id === job.assignee?.id;
              const cellClass = showHistory ? 'with-history' : '';
              const showASOneButton = !job.asOneLocked && jobIsScheduled(job.status);
              const additionalActionsOpen = hiddenButtonsVisible === job.id;
              const additionalActionsChevronClassName = cn(
                'transition-all',
                additionalActionsOpen ? 'rotate-90' : '-rotate-90',
              );
              const toggleAdditionalActions = (e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                setHiddenButtonsVisible(additionalActionsOpen ? null : job.id);
              };

              const getASOneLinkPrefix = () => {
                return `${window.location.origin}/as-one/job/`;
              };

              const unlockJob = (id: string, name: string) => {
                openModal(ModalNamesEnum.UnlockJob, {
                  onCancel: closeModal,
                  onSuccess: () => {
                    closeModal();
                    reload();
                  },
                  isSubmitting: false,
                  id,
                  name,
                });
              };

              return (
                <React.Fragment key={job.id ?? index}>
                  <Tr className="max-md:relative">
                    {showCheckboxColumn && (
                      <Td className="core-table-selection max-md:absolute max-md:right-4 max-md:top-4">
                        <div>
                          <div className="flex gap-3 items-center">
                            <CheckBox
                              name={`select-job-${job.id}`}
                              disabled={job.asOneLocked}
                              checked={checkedJobs.includes(job.id)}
                              onChange={() => checkJob(job.id)}
                            />
                          </div>
                        </div>
                      </Td>
                    )}
                    <Td className="core-table-title">
                      <div>
                        <div className="flex gap-3 items-center max-w-full">
                          {job.asOneLocked ? <LockedJob className="flex-shrink-0 pl-2" width="30" /> : null}
                          <div className="max-w-full">
                            <JobName className="max-w-full" data-testid={`jobTitle${job.name}`}>
                              {job.asOneLocked ? (
                                job.name || 'Unnamed'
                              ) : (
                                <div className="flex flex-row max-w-full">
                                  {job.meetingId ? <TagAccent className="mr-2 text-[10px]">AS ONE</TagAccent> : null}
                                  <StyledLink
                                    className="max-w-full text-ellipsis overflow-hidden"
                                    to={generateJobLink(job.id, job.status)}
                                  >
                                    {job.name || 'Unnamed'}
                                  </StyledLink>
                                </div>
                              )}
                            </JobName>
                            <IdField>{job.id}</IdField>
                            <TagsField>
                              {[...(job.tags ?? []), ...(job.noShow ? ['no_show'] : [])].map((tag, i) => {
                                return (
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setSearch(`#${tag}`);
                                    }}
                                    key={`${job.id}-${i}`}
                                  >
                                    #{tag}
                                  </a>
                                );
                              })}
                            </TagsField>
                          </div>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <DeadlineCell>
                        {!job.deadline && <p className="deadLine"></p>}
                        {job.deadline && <p className="deadLine">{formatIsoDateToAmerican(job.deadline)}</p>}
                        {!!organization?.permissions?.submitJobs && (
                          <PencilIcon
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                            onClick={() => editDeadline(job)}
                          />
                        )}
                      </DeadlineCell>
                    </Td>
                    <Td>
                      <AssignCell>
                        {!assignee ? <div className="assignee"></div> : null}
                        {assignee ? (
                          <Tooltip key={index} content={assignee?.email || ''} placement="bottom">
                            <div className="assignee">
                              <AssignLink to={routes.userDetails.make(organization?.slug ?? '', assignee?.id)}>
                                <p className="assignName">
                                  {assignee?.name} {assignee?.lastname}
                                </p>
                                <p className="assignEmail">{assignee?.email || ''}</p>
                              </AssignLink>
                            </div>
                          </Tooltip>
                        ) : null}
                        {showEditAssigneeButton ? (
                          <PencilIcon
                            className="text-[var(--color-text-muted)] hover:text-[var(--color-accent)] cursor-pointer"
                            onClick={() => editAssignee(job)}
                          />
                        ) : null}
                      </AssignCell>
                    </Td>
                    <Td>
                      <p>{getAmericanTime(job.createdAt)}</p>
                    </Td>
                    <Td>
                      <p>
                        {job.reportingType === REPORTING_TYPE_ENUM.REPORTING_TYPE_FTR
                          ? 'To be analyzed'
                          : convertDuration(parseInt((job?.duration ?? 0).toString(), 10))}
                      </p>
                    </Td>
                    <Td>
                      <StatusCell className={cellClass}>
                        <StatusCellContent>
                          <JobStatusText testid={`jobStatus${job.name}`} status={job.status} removed={job.removed} />
                          <StatusDate>{formatDateMsDsYHI(job.statusChangeDate ?? '')}</StatusDate>
                        </StatusCellContent>
                        {showHistory ? (
                          <IconButtonClean onClick={() => handleShowHistory(job)}>
                            <HistoryIcon />
                          </IconButtonClean>
                        ) : null}
                      </StatusCell>
                    </Td>
                    <Td>
                      <JobStatusActionsButton job={job} reload={reload} />
                    </Td>
                    <Td style={{ width: '1%' }}>
                      <IconsWrapper className="empty:hidden">
                        {job.asOneLocked && job.status === 'JOB_STATUS_LOCKED' ? (
                          <OutlinedButton className="unlock-button" onClick={() => unlockJob(job.id, job.name)}>
                            Unlock now
                          </OutlinedButton>
                        ) : null}
                        {showASOneButton ? (
                          <IconButton
                            title="Open in AutoScriptOne"
                            onClick={() =>
                              window.open(`${getASOneLinkPrefix()}${job.meetingId}`, '_blank', 'noopener,noreferrer')
                            }
                          >
                            <ASOneIcon />
                          </IconButton>
                        ) : null}
                        {!job.asOneLocked &&
                        canShowButtonsTRow(job, isVsAdmin, canManageWorkspaces, organization, me) ? (
                          <>
                            <IconButton
                              title="Open additional actions"
                              aria-expanded={additionalActionsOpen}
                              className="max-md:hidden"
                              onClick={toggleAdditionalActions}
                            >
                              <ChevronLeft className={additionalActionsChevronClassName} />
                            </IconButton>
                            <button
                              type="button"
                              aria-expanded={additionalActionsOpen}
                              className="flex min-h-11 items-center gap-1.5 text-xs font-semibold text-link md:hidden"
                              onClick={toggleAdditionalActions}
                            >
                              {additionalActionsOpen ? 'Hide actions' : 'More actions'}
                              <ChevronLeft className={cn('h-3 w-3', additionalActionsChevronClassName)} />
                            </button>
                          </>
                        ) : null}
                      </IconsWrapper>
                    </Td>
                  </Tr>

                  {hiddenButtonsVisible === job.id ? <ButtonsTRow job={job} reload={reload} /> : null}
                  {renderInlineEditRow?.(job)}
                </React.Fragment>
              );
            })}
          </Tbody>
        </Table>
      </TableWrapper>
      <PagePagination
        totalItemsCount={jobs?.count ?? 0}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </>
  );
};
